<template>
	<WaitDialog :isActive="loading" title="Loading..." />

	<v-container fluid class="group-management-container d-flex flex-column">
		<!-- App Context Toggle -->
		<v-row class="flex-grow-0">
			<v-col cols="12">
				<v-btn-toggle v-model="appContext" mandatory color="primary" density="comfortable">
					<v-btn value="POINT">POINT</v-btn>
					<v-btn value="Operation">Operation</v-btn>
				</v-btn-toggle>
			</v-col>
		</v-row>

		<v-row class="mt-2 flex-grow-1" style="overflow: hidden; min-height: 0;">
			<!-- Groups Table -->
			<v-col cols="12" md="5" class="d-flex" style="height: 100%;">
				<v-card class="text-left d-flex flex-column" style="width: 100%; overflow: hidden;">
					<v-data-table
						:headers="groupHeaders"
						:items="groups"
						:loading="loading"
						density="comfortable"
						class="elevation-0 flex-grow-1"
						style="overflow-y: auto;"
						no-data-text="No groups found"
						items-per-page="-1"
						hide-default-footer
						hover
						fixed-header
						height="100%"
						@click:row="(_, row) => selectGroup(row.item)"
					>
						<template #top>
							<v-toolbar flat>
								<v-toolbar-title>Groups</v-toolbar-title>
								<v-divider class="mx-4" inset vertical />
								<m-btn color="primary" prepend-icon="mdi-plus" @click="openAddGroupDialog">Add</m-btn>
								<m-btn class="ml-2" color="primary" prepend-icon="mdi-merge" @click="openMergeDialog">Merge</m-btn>
								<m-btn class="ml-2" color="primary" prepend-icon="mdi-refresh" @click="loadGroups">Refresh</m-btn>
							</v-toolbar>
						</template>
						<!-- eslint-disable vue/valid-v-slot -->
						<template v-slot:item.actions="{ item }">
							<v-btn icon size="small" variant="text" color="primary" @click.stop="openEditGroupDialog(item)" title="Edit">
								<v-icon>mdi-pencil</v-icon>
							</v-btn>
							<v-btn icon size="small" variant="text" color="error" @click.stop="confirmDeleteGroup(item)" title="Delete">
								<v-icon>mdi-trash-can-outline</v-icon>
							</v-btn>
						</template>

						<template v-slot:item.groupId="{ item }">
							<span :class="{ 'font-weight-bold': selectedGroup?.groupId === item.groupId }">
								{{ item.groupId }}
							</span>
						</template>
					</v-data-table>
				</v-card>
			</v-col>

			<!-- User Assignment -->
			<v-col cols="12" md="7" v-if="selectedGroup" class="d-flex" style="height: 100%;">
				<v-card class="text-left d-flex flex-column" style="width: 100%; overflow: hidden;">
					<v-overlay
						:model-value="loadingUsers"
						contained
						class="align-center justify-center"
					>
						<v-progress-circular indeterminate color="primary" />
					</v-overlay>
					<v-card-title class="d-flex align-center">
						Users in Group: {{ selectedGroup.groupId }}
						<v-chip class="ml-2" size="small" color="primary">{{ assignedUsers.length }} assigned</v-chip>
						<span v-if="hasUserChanges" class="ml-2 text-warning text-caption">(unsaved changes)</span>
						<v-spacer />
						<m-btn color="primary" size="small" :disabled="!hasUserChanges" @click="saveUserChanges" class="mr-2">
							<v-icon start>mdi-content-save</v-icon> Save
						</m-btn>
						<m-btn size="small" :disabled="!hasUserChanges" @click="resetUserChanges">
							<v-icon start>mdi-refresh</v-icon> Reset
						</m-btn>
					</v-card-title>
					<v-card-subtitle class="text-caption">
						Users in other groups are shown with a group indicator.
						For bulk or cross-group assignment, use the "Assign Users to Groups" page.
					</v-card-subtitle>
					<v-card-text class="d-flex flex-column" style="flex: 1; overflow: hidden; min-height: 0;">
						<!-- Fixed header: labels, search, and transfer buttons -->
						<v-row class="flex-grow-0" align="end">
							<v-col cols="5">
								<div class="text-subtitle-2 mb-2">Available Users</div>
								<v-text-field
									v-model="availableUserSearch"
									prepend-inner-icon="mdi-magnify"
									label="Search"
									variant="outlined"
									density="compact"
									hide-details
									clearable
								/>
							</v-col>
							<v-col cols="2" class="d-flex flex-column align-center">
								<m-btn
									class="mb-2"
									color="primary"
									size="small"
									:disabled="!selectedAvailableUser"
									@click="addUserLocal"
									block
								>
									<v-icon>mdi-chevron-right</v-icon> Add
								</m-btn>
								<m-btn
									color="error"
									size="small"
									:disabled="!selectedAssignedUser"
									@click="removeUserLocal"
									block
								>
									<v-icon>mdi-chevron-left</v-icon> Remove
								</m-btn>
							</v-col>
							<v-col cols="5">
								<div class="text-subtitle-2 mb-2">Assigned Users</div>
								<v-text-field
									v-model="assignedUserSearch"
									prepend-inner-icon="mdi-magnify"
									label="Search"
									variant="outlined"
									density="compact"
									hide-details
									clearable
								/>
							</v-col>
						</v-row>

						<!-- Scrollable user lists -->
						<v-row class="flex-grow-1 mt-2" style="overflow: hidden; min-height: 0;">
							<v-col cols="5" class="d-flex" style="height: 100%;">
								<v-list density="compact" class="user-list" lines="two">
									<v-list-item
										v-for="user in filteredAvailableUsers"
										:key="user.userId"
										:value="user.userId"
										:active="selectedAvailableUser?.userId === user.userId"
										@click="selectedAvailableUser = user"
									>
										<v-list-item-title>{{ displayUser(user) }}</v-list-item-title>
										<v-list-item-subtitle v-if="user.groupId" class="text-caption text-warning">
											Currently in: {{ user.groupId }}
										</v-list-item-subtitle>
									</v-list-item>
								</v-list>
							</v-col>
							<v-col cols="2" />
							<v-col cols="5" class="d-flex" style="height: 100%;">
								<v-list density="compact" class="user-list" lines="one">
									<v-list-item
										v-for="user in filteredAssignedUsers"
										:key="user.userId"
										:value="user.userId"
										:active="selectedAssignedUser?.userId === user.userId"
										@click="selectedAssignedUser = user"
									>
										<v-list-item-title>{{ displayUser(user) }}</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<!-- Add/Edit Group Dialog -->
		<v-dialog v-model="showGroupDialog" max-width="500" persistent>
			<v-card>
				<v-card-title>{{ editingGroup ? 'Edit Group' : 'Add Group' }}</v-card-title>
				<v-card-text>
					<v-text-field
						v-model="groupForm.groupName"
						label="Group Name *"
						variant="outlined"
						density="comfortable"
						maxlength="10"
						:disabled="!!editingGroup"
						class="mb-2"
					/>
					<v-text-field
						v-model="groupForm.groupDesc"
						label="Description *"
						variant="outlined"
						density="comfortable"
						maxlength="64"
					/>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<m-btn @click="showGroupDialog = false" text="Cancel" />
					<m-btn
						color="primary"
						@click="submitGroup"
						:text="editingGroup ? 'Update' : 'Add'"
						:disabled="!groupForm.groupName || !groupForm.groupDesc"
					/>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Delete Confirm Dialog -->
		<v-dialog v-model="showDeleteDialog" max-width="400">
			<v-card>
				<v-card-title>Confirm Delete</v-card-title>
				<v-card-text>Are you sure you want to delete group "{{ deletingGroup?.groupId }}"?</v-card-text>
				<v-card-actions>
					<v-spacer />
					<m-btn @click="showDeleteDialog = false" text="Cancel" />
					<m-btn color="error" @click="deleteGroup" text="Delete" />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Merge Groups Dialog -->
		<v-dialog v-model="showMergeDialog" max-width="500" persistent>
			<v-card>
				<v-card-title>Merge Groups</v-card-title>
				<v-card-text>
					<p class="text-body-2 mb-4">
						Move all members from the source group into the target group.
					</p>
					<v-select
						v-model="mergeForm.sourceGroup"
						:items="groups"
						item-title="groupId"
						:item-value="item => item"
						label="Source Group (merge from)"
						variant="outlined"
						density="comfortable"
						class="mb-1"
					>
						<template #item="{ item, props }">
							<v-list-item v-bind="props">
								<template #subtitle>{{ item.raw.groupDesc }}</template>
							</v-list-item>
						</template>
					</v-select>
					<div v-if="sourceMemberCount !== null" class="text-caption mb-3 ml-1">
						<v-chip size="x-small" color="info" class="mr-1">{{ sourceMemberCount }}</v-chip> member{{ sourceMemberCount === 1 ? '' : 's' }} in source group
					</div>
					<v-select
						v-model="mergeForm.targetGroup"
						:items="mergeTargetGroups"
						item-title="groupId"
						:item-value="item => item"
						label="Target Group (merge into)"
						variant="outlined"
						density="comfortable"
						class="mb-1"
					>
						<template #item="{ item, props }">
							<v-list-item v-bind="props">
								<template #subtitle>{{ item.raw.groupDesc }}</template>
							</v-list-item>
						</template>
					</v-select>
					<div v-if="targetMemberCount !== null" class="text-caption mb-3 ml-1">
						<v-chip size="x-small" color="info" class="mr-1">{{ targetMemberCount }}</v-chip> member{{ targetMemberCount === 1 ? '' : 's' }} in target group
					</div>
					<v-checkbox
						v-model="mergeForm.deleteSource"
						label="Delete source group after merge"
						density="comfortable"
						hide-details
					/>
				</v-card-text>
				<v-alert v-if="mergeResultCount !== null" type="success" variant="tonal" density="compact" class="mx-4 mb-2">
					Merge complete — target group now has <strong>{{ mergeResultCount }}</strong> member{{ mergeResultCount === 1 ? '' : 's' }}.
				</v-alert>
				<v-card-actions>
					<v-spacer />
					<m-btn v-if="mergeResultCount !== null" @click="showMergeDialog = false" text="Close" />
					<template v-else>
						<m-btn @click="showMergeDialog = false" text="Cancel" />
						<m-btn
							color="primary"
							@click="executeMerge"
							text="Merge"
							:disabled="!mergeForm.sourceGroup || !mergeForm.targetGroup"
						/>
					</template>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script lang="ts">
import { ref, computed, watch, inject } from 'vue';
import { useTableMaintenanceApi } from '@/composables/UseTableMaintenanceApi';
import { useGroupMemberAssignment } from '@/composables/UseGroupMemberAssignment';
import { useCurrentUser } from '@/composables/UseCurrentUser';
import { displayUser } from '@/utils/display-formatters';
import type { GroupItem, UserProfile } from '@/models/TableMaintenanceViewModels';
import MBtn from '@/components/BaseButton.vue';
import WaitDialog from '@/components/WaitDialog.vue';
import { Tell } from '@/services/Tell';

export default {
	name: 'GroupManagement',
	components: { MBtn, WaitDialog },
	route: {
		path: '/SSAM/groups',
		meta: {
			requiresAuth: true,
			title: 'Group Management',
		},
	},

	setup() {
		const api = useTableMaintenanceApi();
		const tell = inject('tell') as Tell;
		const { getCurrentUser } = useCurrentUser();

		// ── State ──────────────────────────────────────────────────────────
		const loading = ref(false);
		const loadingUsers = ref(false);
		const appContext = ref('POINT');

		const groups = ref<GroupItem[]>([]);
		const selectedGroup = ref<GroupItem | null>(null);

		// ── Group member assignment ────────────────────────────────────────
		const {
			allUsers, assignedUsers, availableUsers, hasChanges: hasUserChanges,
			loadAllUsers, loadGroupMembers, clearMembers, addToAssigned, removeFromAssigned,
			saveMembers, resetMembers,
		} = useGroupMemberAssignment(appContext, api, tell);

		const selectedAvailableUser = ref<UserProfile | null>(null);
		const selectedAssignedUser = ref<UserProfile | null>(null);
		const availableUserSearch = ref('');
		const assignedUserSearch = ref('');

		const showGroupDialog = ref(false);
		const editingGroup = ref<GroupItem | null>(null);
		const groupForm = ref({ groupName: '', groupDesc: '' });

		const showDeleteDialog = ref(false);
		const deletingGroup = ref<GroupItem | null>(null);

		const showMergeDialog = ref(false);
		const mergeForm = ref<{ sourceGroup: GroupItem | null; targetGroup: GroupItem | null; deleteSource: boolean }>({
			sourceGroup: null, targetGroup: null, deleteSource: true,
		});

		const sourceMemberCount = ref<number | null>(null);
		const targetMemberCount = ref<number | null>(null);
		const mergeResultCount = ref<number | null>(null);

		const groupHeaders = [
			{ title: 'Group ID', key: 'groupId', sortable: true },
			{ title: 'Description', key: 'groupDesc', sortable: true },
			{ title: '', key: 'actions', sortable: false, width: 100, align: 'center' as const },
		];

		// ── Computed ───────────────────────────────────────────────────────
		const filteredAvailableUsers = computed(() => {
			const search = (availableUserSearch.value ?? '').toLowerCase();
			const sorted = [...availableUsers.value].sort((a, b) => displayUser(a).localeCompare(displayUser(b)));
			if (!search) return sorted;
			return sorted.filter(u => displayUser(u).toLowerCase().includes(search));
		});

		const filteredAssignedUsers = computed(() => {
			const search = (assignedUserSearch.value ?? '').toLowerCase();
			const sorted = [...assignedUsers.value].sort((a, b) => displayUser(a).localeCompare(displayUser(b)));
			if (!search) return sorted;
			return sorted.filter(u => displayUser(u).toLowerCase().includes(search));
		});

		const mergeTargetGroups = computed(() => {
			if (!mergeForm.value.sourceGroup) return groups.value;
			return groups.value.filter(g => g.groupId !== mergeForm.value.sourceGroup?.groupId);
		});

		// ── Data loading ───────────────────────────────────────────────────
		async function loadGroups() {
			loading.value = true;
			try {
				groups.value = await api.getGroups(appContext.value);
				selectedGroup.value = null;
				clearMembers();
			} catch (e: unknown) {
				tell.error(`Error loading groups: ${(e as Error).message}`);
			} finally {
				loading.value = false;
			}
		}

		async function selectGroup(group: GroupItem) {
			selectedGroup.value = group;
			selectedAvailableUser.value = null;
			selectedAssignedUser.value = null;
			loadingUsers.value = true;
			// Load group members and all users concurrently (composable handles
			// both so the Available list is never empty due to a race condition).
			await Promise.all([loadGroupMembers(group.groupId), loadAllUsers()]);
			loadingUsers.value = false;
		}

		// ── Group CRUD ─────────────────────────────────────────────────────
		function openAddGroupDialog() {
			editingGroup.value = null;
			groupForm.value = { groupName: '', groupDesc: '' };
			showGroupDialog.value = true;
		}

		function openEditGroupDialog(group: GroupItem) {
			editingGroup.value = group;
			groupForm.value = { groupName: group.groupId, groupDesc: group.groupDesc };
			showGroupDialog.value = true;
		}

		async function submitGroup() {
			loading.value = true;
			try {
				const user = await getCurrentUser();
				const result = editingGroup.value
					? await api.updateGroup({
						user, appContext: appContext.value,
						oldGroupName: editingGroup.value.groupId,
						newGroupName: groupForm.value.groupName,
						groupDesc: groupForm.value.groupDesc,
					})
					: await api.addGroup({
						user, appContext: appContext.value,
						groupName: groupForm.value.groupName,
						groupDesc: groupForm.value.groupDesc,
					});
				// The API throws (with its own message) on failure, so reaching here
				// means success. Only then do we close the dialog and refresh — on
				// error the dialog stays open so the user can correct and retry.
				tell.success(result.message);
				showGroupDialog.value = false;
				await loadGroups();
			} catch (e: unknown) {
				tell.error((e as Error).message);
			} finally {
				loading.value = false;
			}
		}

		function confirmDeleteGroup(group: GroupItem) {
			deletingGroup.value = group;
			showDeleteDialog.value = true;
		}

		async function deleteGroup() {
			if (!deletingGroup.value) return;
			loading.value = true;
			try {
				const result = await api.deleteGroup(deletingGroup.value.groupId, appContext.value, await getCurrentUser());
				// Close and clear only on success so a failed delete keeps the dialog
				// open and the confirm button functional for retry.
				showDeleteDialog.value = false;
				deletingGroup.value = null;
				tell.success(result.message);
				await loadGroups();
			} catch (e: unknown) {
				tell.error((e as Error).message);
			} finally {
				loading.value = false;
			}
		}

		// ── User Assignment ────────────────────────────────────────────────
		// Local staging: Add / Remove modify the working copy only.
		// The server is only updated when the user clicks Save.

		function addUserLocal() {
			if (!selectedAvailableUser.value) return;
			addToAssigned([selectedAvailableUser.value]);
			selectedAvailableUser.value = null;
		}

		function removeUserLocal() {
			if (!selectedAssignedUser.value) return;
			removeFromAssigned([selectedAssignedUser.value.userId]);
			selectedAssignedUser.value = null;
		}

		function resetUserChanges() {
			resetMembers();
			selectedAvailableUser.value = null;
			selectedAssignedUser.value = null;
		}

		async function saveUserChanges() {
			if (!selectedGroup.value) return;
			loadingUsers.value = true;
			await saveMembers(selectedGroup.value.groupId, await getCurrentUser());
			loadingUsers.value = false;
		}

		// ── Merge Groups ───────────────────────────────────────────────────────
		function openMergeDialog() {
			mergeForm.value = { sourceGroup: null, targetGroup: null, deleteSource: false };
			sourceMemberCount.value = null;
			targetMemberCount.value = null;
			mergeResultCount.value = null;
			showMergeDialog.value = true;
		}

		watch(() => mergeForm.value.sourceGroup, async (group) => {
			if (!group) { sourceMemberCount.value = null; return; }
			try {
				const members = await api.getGroupUsers(group.groupId, appContext.value);
				sourceMemberCount.value = members.length;
			} catch (e) {
				sourceMemberCount.value = null;
				console.warn('Could not load source group member count', e);
			}
		});

		watch(() => mergeForm.value.targetGroup, async (group) => {
			if (!group) { targetMemberCount.value = null; return; }
			try {
				const members = await api.getGroupUsers(group.groupId, appContext.value);
				targetMemberCount.value = members.length;
			} catch (e) {
				targetMemberCount.value = null;
				console.warn('Could not load target group member count', e);
			}
		});

		async function executeMerge() {
			if (!mergeForm.value.sourceGroup || !mergeForm.value.targetGroup) return;
			loading.value = true;
			try {
				const result = await api.mergeGroups({
					user: await getCurrentUser(),
					appContext: appContext.value,
					sourceGroupName: mergeForm.value.sourceGroup.groupId,
					targetGroupName: mergeForm.value.targetGroup.groupId,
					deleteSource: mergeForm.value.deleteSource,
				});
				tell.success(result.message);
				const targetId = mergeForm.value.targetGroup.groupId;
				await Promise.all([loadGroups(), loadAllUsers()]);
				// Re-select the target group so its combined membership is shown.
				const merged = groups.value.find(g => g.groupId === targetId);
				if (merged) await selectGroup(merged);
				// Show result count in the dialog
				try {
					const targetMembers = await api.getGroupUsers(targetId, appContext.value);
					mergeResultCount.value = targetMembers.length;
				} catch (e) {
					mergeResultCount.value = null;
					console.warn('Could not load target member count after merge', e);
				}
				sourceMemberCount.value = null;
			} catch (e: unknown) {
				tell.error((e as Error).message);
			} finally {
				loading.value = false;
			}
		}

		// ── Watchers ───────────────────────────────────────────────────────
		watch(appContext, async () => {
			selectedGroup.value = null;
			clearMembers();
			await loadGroups();
		}, { immediate: true });

		return {
			loading, loadingUsers, appContext,
			groups, selectedGroup, allUsers, assignedUsers,
			hasUserChanges,
			selectedAvailableUser, selectedAssignedUser,
			availableUserSearch, assignedUserSearch,
			filteredAvailableUsers, filteredAssignedUsers,
			showGroupDialog, editingGroup, groupForm,
			showDeleteDialog, deletingGroup,
			showMergeDialog, mergeForm, mergeTargetGroups,
			sourceMemberCount, targetMemberCount, mergeResultCount,
			groupHeaders, displayUser,
			loadGroups, selectGroup,
			openAddGroupDialog, openEditGroupDialog,
			submitGroup, confirmDeleteGroup, deleteGroup,
			addUserLocal, removeUserLocal, saveUserChanges, resetUserChanges,
			openMergeDialog, executeMerge,
		};
	},
};
</script>

<style scoped>
.group-management-container {
	height: calc(100vh - 64px);
}

.user-list {
	flex: 1;
	width: 100%;
	overflow-y: auto;
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-radius: 4px;
}
</style>
